import React, { useMemo, useState } from 'react';
import { StepProps } from '../MultiStepModal';

export default function CertificateTemplateSchemaPreviewStep({ data }: StepProps) {
  const [showStructure, setShowStructure] = useState(false);
  const schema = (data as any)?.inferredSchema;
  const documentIntel = (data as any)?.documentIntel;
  const lowConfidenceCount = Number((data as any)?.lowConfidenceCount ?? 0);
  const lowConfidenceFields: any[] = Array.isArray((data as any)?.lowConfidenceFields)
    ? (data as any).lowConfidenceFields
    : [];

  const screens = useMemo(() => {
    const arr: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
    return arr;
  }, [schema]);

  const totalFieldCount = useMemo(() => {
    let n = 0;
    for (const s of screens) {
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      for (const sec of sections) {
        const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
        n += fields.length;
      }
    }
    return n;
  }, [screens]);

  const rgCount = useMemo(() => {
    const rg = (schema as any)?.repeatableGroups;
    return Array.isArray(rg) ? rg.length : 0;
  }, [schema]);

  const contextFieldCount = useMemo(() => {
    let n = 0;
    for (const s of screens) {
      const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
      for (const sec of sections) {
        const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
        for (const f of fields) {
          if (String(f?.context || '').trim()) n += 1;
        }
      }
    }
    return n;
  }, [screens]);

  if (!schema) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Auto-generated form</h3>
          <p className="step-form-subtitle">No schema generated yet. Go back and extract fields.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Auto-generated form</h3>
        <p className="step-form-subtitle">
          We’ve generated a multi-screen form from your PDF. You’ll only need to review the low-confidence items.
        </p>
      </div>

      <div className="step-form-content">
        {documentIntel && typeof documentIntel === 'object' ? (
          <label className="mb-3 flex cursor-pointer select-none items-center gap-2 text-sm text-[#383838]">
            <input type="checkbox" className="rounded border-[#C9C7C3]" checked={showStructure} onChange={(e) => setShowStructure(e.target.checked)} />
            <span>Show document structure (zones, tables, layout signature — for debugging remote parses)</span>
          </label>
        ) : null}

        {showStructure && documentIntel ? (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-800">
            <div className="font-semibold text-slate-900">Layout engine</div>
            <div className="mt-1 font-mono text-[11px] break-all">buildTag: {String(documentIntel?.buildTag || '—')}</div>
            <div className="mt-1 font-mono text-[11px] break-all">layoutSignature: {String(documentIntel?.layoutSignature || '—')}</div>
            <div className="mt-2 text-slate-600">Pages: {Number(documentIntel?.pageCount || 0)}</div>
            <div className="mt-2">
              <span className="font-semibold">Zones</span>
              <ul className="mt-1 list-inside list-disc space-y-0.5 text-slate-700">
                {(Array.isArray(documentIntel.zones) ? documentIntel.zones : []).slice(0, 40).map((z: any, i: number) => (
                  <li key={String(z?.id || i)}>
                    {String(z?.id || 'zone')} — {String(z?.type || '?')} (p.{Number(z?.page ?? 0) + 1}, conf.{' '}
                    {Math.round(Number(z?.confidence || 0) * 100)}%)
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-2 text-slate-600">
              Layout checklists: {Array.isArray(documentIntel?.checklists) ? documentIntel.checklists.length : 0}
            </div>
            {documentIntel?.parsedDocument ? (
              <div className="mt-2 rounded border border-emerald-200 bg-emerald-50/80 p-2 text-emerald-950">
                <span className="font-semibold">ParsedDocument (v2)</span>
                <div className="mt-1 text-[11px]">
                  meta: {String(documentIntel.parsedDocument?.meta?.documentType || '—')}
                  {documentIntel.parsedDocument?.meta?.jobNumber
                    ? ` · job ${String(documentIntel.parsedDocument.meta.jobNumber)}`
                    : ''}
                </div>
                <div className="mt-1">
                  applianceGroups: {Array.isArray(documentIntel.parsedDocument?.applianceGroups) ? documentIntel.parsedDocument.applianceGroups.length : 0} ·
                  checklists: {Array.isArray(documentIntel.parsedDocument?.checklists) ? documentIntel.parsedDocument.checklists.length : 0} ·
                  sections: {Array.isArray(documentIntel.parsedDocument?.sections) ? documentIntel.parsedDocument.sections.length : 0} · freeform:{' '}
                  {Array.isArray(documentIntel.parsedDocument?.freeform) ? documentIntel.parsedDocument.freeform.length : 0}
                </div>
                {Array.isArray(documentIntel.parsedDocument?.applianceGroups) && documentIntel.parsedDocument.applianceGroups.length ? (
                  <details className="mt-2 text-[11px]">
                    <summary className="cursor-pointer text-emerald-900">Appliance groups (units as columns)</summary>
                    <ul className="mt-1 list-inside list-disc space-y-1 text-emerald-950">
                      {documentIntel.parsedDocument.applianceGroups.map((g: any, i: number) => (
                        <li key={String(g?.groupId || i)}>
                          {String(g?.groupId || 'group')}:{' '}
                          {Array.isArray(g?.units) ? g.units.length : 0} unit(s) —{' '}
                          {Array.isArray(g?.units)
                            ? g.units
                                .map((u: any) => String(u?.name || ''))
                                .filter(Boolean)
                                .slice(0, 5)
                                .join(', ')
                            : '—'}
                          {Array.isArray(g?.units) && g.units.length > 5 ? '…' : ''}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}
            <div className="mt-2">
              <span className="font-semibold">Tables</span>
              <ul className="mt-1 list-inside list-disc space-y-1 text-slate-700">
                {(Array.isArray(documentIntel.tables) ? documentIntel.tables : []).slice(0, 20).map((t: any, i: number) => (
                  <li key={String(t?.id || i)}>
                    {String(t?.id || 'table')}{' '}
                    <span className="text-slate-500">shape: {String(t?.tableShape || '—')}</span>
                    {Array.isArray(t?.columnHeaders) && t.columnHeaders.length ? (
                      <div className="ml-2 mt-0.5 font-mono text-[10px] text-slate-600">
                        headers: {t.columnHeaders.slice(0, 8).join(' · ')}
                        {t.columnHeaders.length > 8 ? '…' : ''}
                      </div>
                    ) : null}
                    {Array.isArray(t?.rowLabels) && t.rowLabels.length ? (
                      <div className="ml-2 mt-0.5 font-mono text-[10px] text-slate-600">
                        row labels: {t.rowLabels.slice(0, 6).join(' · ')}
                        {t.rowLabels.length > 6 ? '…' : ''}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-slate-600">Raw documentIntel JSON (truncated)</summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded border border-slate-200 bg-white p-2 text-[10px] leading-relaxed">
                {JSON.stringify(
                  { ...documentIntel, elements: '(omitted)', normalized: documentIntel?.normalized ? '(omitted)' : undefined },
                  null,
                  2,
                ).slice(0, 12000)}
              </pre>
            </details>
          </div>
        ) : null}

        <div className="rounded-xl border border-[#E6E6E4] bg-white p-4">
          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-5">
            <div>
              <div className="text-xs text-[#686E6C]">Screens</div>
              <div className="mt-1 font-medium text-[#181818]">{screens.length}</div>
            </div>
            <div>
              <div className="text-xs text-[#686E6C]">Fields</div>
              <div className="mt-1 font-medium text-[#181818]">{totalFieldCount}</div>
            </div>
            <div>
              <div className="text-xs text-[#686E6C]">Low confidence</div>
              <div className="mt-1 font-medium text-[#181818]">{lowConfidenceCount}</div>
            </div>
            <div>
              <div className="text-xs text-[#686E6C]">Fields with context</div>
              <div className="mt-1 font-medium text-[#181818]">{contextFieldCount}</div>
            </div>
            <div>
              <div className="text-xs text-[#686E6C]">Repeatable groups</div>
              <div className="mt-1 font-medium text-[#181818]">{rgCount}</div>
            </div>
          </div>

          {screens.length ? (
            <div className="mt-4">
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9CA3AF]">Screen order</div>
              <div className="mt-2 space-y-2">
                {screens.map((s: any) => (
                  <div key={String(s?.key || '')} className="text-sm text-[#383838]">
                    {String(s?.title || s?.key || 'Screen')}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {screens.length ? (
          <div className="mt-4 space-y-3">
            {screens.map((s: any) => {
              const screenTitle = String(s?.title || s?.key || 'Screen');
              const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
              return (
                <div key={String(s?.key || screenTitle)} className="rounded-xl border border-[#E6E6E4] bg-white p-4">
                  <div className="text-sm font-semibold text-[#181818]">{screenTitle}</div>
                  {sections.length ? (
                    <div className="mt-3 space-y-3">
                      {sections.map((sec: any) => {
                        const secTitle = String(sec?.title || sec?.key || 'Section');
                        const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
                        return (
                          <div key={String(sec?.key || secTitle)} className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">
                              {secTitle}
                            </div>
                            <div className="mt-2 space-y-2">
                              {fields.slice(0, 40).map((f: any, idx: number) => (
                                <div key={String(f?.fieldSignature || f?.pdfFieldName || idx)} className="text-xs">
                                  <div className="font-medium text-[#181818] truncate">
                                    {String(f?.label || f?.pdfFieldName || 'Field')}
                                  </div>
                                  {String(f?.context || '').trim() ? (
                                    <div className="mt-0.5 text-[#686E6C]">{String(f.context)}</div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-[#686E6C]">No sections found on this screen.</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {lowConfidenceFields.length ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-900">Needs review</div>
            <div className="mt-1 text-xs text-amber-900/80">
              These fields are below the confidence threshold and may need user confirmation.
            </div>
            <div className="mt-3 space-y-2">
              {lowConfidenceFields.slice(0, 30).map((f: any, idx: number) => (
                <div key={String(f?.pdfFieldName || idx)} className="flex items-center justify-between gap-3 text-xs">
                  <div className="min-w-0 truncate text-amber-950">{String(f?.pdfFieldName || 'Field')}</div>
                  <div className="shrink-0 text-amber-900/80">{Math.round(Number(f?.confidence || 0) * 100)}%</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
